import type { BeehiveSite, UserResponse } from '@/auth/type';

export type PendoVisitorPayload = Record<string, string | boolean | number>;

const FIRST_SEEN_STORAGE_KEY = 'pendo_first_seen_at';

/**
 * Returns a durable "first visit" timestamp for this browser. Stored in
 * localStorage so it survives across Pendo cookie resets (Pendo's own
 * first-visit concept resets when cookies are cleared).
 *
 * SSR-safe: returns `undefined` when window/localStorage isn't available.
 * localStorage access can throw in private browsing modes, so wrap in try/catch.
 */
function getFirstSeenAt(): number | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const existing = window.localStorage.getItem(FIRST_SEEN_STORAGE_KEY);
    if (existing) {
      const parsed = Number(existing);
      if (Number.isFinite(parsed)) return parsed;
    }
    const now = Date.now();
    window.localStorage.setItem(FIRST_SEEN_STORAGE_KEY, String(now));
    return now;
  } catch {
    // localStorage disabled (e.g., Safari private mode). Skip — not worth throwing.
    return undefined;
  }
}

export type PendoAccountPayload = Record<string, string | null | undefined>;

export type PendoIdentityPayload = {
  signature: string;
  visitor: PendoVisitorPayload;
  account?: PendoAccountPayload;
};

export function resolveBeehiveSiteForPendo(
  user: UserResponse,
  explorerSiteId?: string,
  explorerSiteName?: string
): BeehiveSite | null {
  if (explorerSiteId && explorerSiteName && user.authorized_sites?.length) {
    const match = user.authorized_sites.find(
      (s) => s.site_id === explorerSiteId && s.site_name === explorerSiteName
    );
    if (match) {
      return match;
    }
  }
  return user.active_site ?? null;
}

function getUtmParams(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};
  const source = params.get('utm_source');
  const medium = params.get('utm_medium');
  if (source) utm.last_utm_source = source;
  if (medium) utm.last_utm_medium = medium;
  return utm;
}

function buildVisitor(user: UserResponse): PendoVisitorPayload {
  const email = user.email;
  const firstSeenAt = getFirstSeenAt();
  return {
    id: email,
    email,
    full_name: user.username,
    is_chargebee_user: email.toLowerCase().endsWith('@chargebee.com'),
    ...(firstSeenAt !== undefined ? { first_seen_at: firstSeenAt } : {}),
    ...getUtmParams(),
  };
}

function buildAccount(site: BeehiveSite): PendoAccountPayload {
  const account: PendoAccountPayload = {
    id: site.site_id,
    domain: site.site_name,
    site_type: site.site_type,
  };
  if (site.merchant_id) {
    account.merchant_id = site.merchant_id;
  }
  if (site.site_status) {
    account.site_status = site.site_status;
  }
  if (site.site_plan_id) {
    account.site_plan_id = site.site_plan_id;
  }
  if (site.role != null && site.role !== '') {
    account.role = site.role;
  }
  return account;
}

export function buildPendoIdentityPayload(
  user: UserResponse | null | undefined,
  explorerSiteId?: string,
  explorerSiteName?: string
): PendoIdentityPayload {
  if (!user?.email) {
    const firstSeenAt = getFirstSeenAt();
    return {
      signature: 'anonymous',
      visitor: {
        id: 'ANONYMOUS',
        ...(firstSeenAt !== undefined ? { first_seen_at: firstSeenAt } : {}),
        ...getUtmParams(),
      },
    };
  }

  const site = resolveBeehiveSiteForPendo(
    user,
    explorerSiteId,
    explorerSiteName
  );

  if (!site) {
    return {
      signature: `${user.email}|`,
      visitor: buildVisitor(user),
    };
  }

  return {
    signature: `${user.email}|${site.site_id}`,
    visitor: buildVisitor(user),
    account: buildAccount(site),
  };
}
